'use client';

import React from 'react';
import { useUnifiedIcon } from '@/hooks/useUnifiedIcon';
import { ExperienceType } from '../types';
import { typography } from '@/design-system/tokens/typography';

interface ExperienceAmenitiesProps {
  experience: unknown;
  type: ExperienceType;
}

const AmenityItem: React.FC<{
  label: string;
  icon?: string | null;
  id?: string;
}> = ({ label, icon, id }) => {
  const { icon: resolvedIcon } = useUnifiedIcon({
    label,
    icon,
    id,
    iconSize: 16,
    type: 'amenity',
  });

  return (
    <div className='flex items-center gap-2 p-3 bg-gray-50 rounded-lg'>
      <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
        {resolvedIcon ? (
          typeof resolvedIcon === 'string' ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element -- icon URL */}
              <img
                src={resolvedIcon}
                alt={label}
                className='w-4 h-4 object-contain'
              />
            </>
          ) : (
            <div className='text-blue-600 flex items-center justify-center'>
              {resolvedIcon}
            </div>
          )
        ) : (
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            className='text-blue-600'
          >
            <path d='M8 2L9.5 6L8 10L6.5 6L8 2Z' fill='currentColor' />
          </svg>
        )}
      </div>
      <span className='text-sm text-gray-700'>{label}</span>
    </div>
  );
};

export const ExperienceAmenities: React.FC<ExperienceAmenitiesProps> = ({
  experience,
  type,
}) => {
  if (!experience || type !== 'stay') return null;

  const getAmenities = () => {
    const exp = experience as {
      amenities?: Array<
        string | { title?: string; name?: string; icon?: string; _id?: string }
      >;
      facilities?: Array<
        string | { title?: string; name?: string; icon?: string; _id?: string }
      >;
    };
    return exp?.amenities || exp?.facilities || [];
  };

  const getAmenityDetails = (
    amenity:
      | string
      | { title?: string; name?: string; icon?: string; _id?: string }
  ) => {
    if (typeof amenity === 'string') {
      return { label: amenity, icon: null, id: undefined };
    }
    return {
      label: amenity?.title || amenity?.name || '',
      icon: amenity?.icon || null,
      id: amenity?._id,
    };
  };

  const amenities = getAmenities();

  if (!Array.isArray(amenities) || amenities.length === 0) return null;

  return (
    <div className='space-y-4'>
      <h2
        className='font-semibold text-gray-900'
        style={{
          fontSize: typography.size.lg, // 18px
          fontFamily: typography.fonts.primary,
          lineHeight: 'normal',
        }}
      >
        Amenities
      </h2>
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        {amenities.map((amenity, index) => {
          const { label, icon, id } = getAmenityDetails(amenity);
          const key = id || `amenity-${index}`;

          return <AmenityItem key={key} label={label} icon={icon} id={id} />;
        })}
      </div>
    </div>
  );
};
